// frontend/src/ReportsDashboard.tsx - Comprehensive Reporting & Analytics Interface

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { PieChart, Pie, Cell, ResponsiveContainer, RadialBarChart, RadialBar, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList, BarChart, Bar, ReferenceLine } from 'recharts';
import Cookies from 'js-cookie';
import { API_URL } from './apiConfig';
import ComprehensiveAuditReport from './ComprehensiveAuditReport';

// --- Component Props and Interfaces ---

interface ReportsDashboardProps {
  processId: number;
  processName: string;
  userRole: 'ADMIN' | 'PROCESS_OWNER' | 'EVALUATION_PANEL_MEMBER' | 'AUDITOR' | 'SUPER_ADMIN';
  onBack: () => void;
}

// Response interfaces matching backend
interface ProcessSummaryReport {
  process_id: number;
  process_name: string;
  ito_reference: string;
  status: string;
  evaluation_methodology: string; // 'vfm', 'price_quality', 'requirements_based'
  score_scale: string; // '0-5', '0-10'
  total_suppliers: number;
  compliant_suppliers: number;
  total_evaluators: number;
  total_evaluations_possible: number;
  completed_evaluations: number;
  completion_percentage: number;
  average_process_score: number | null;
  created_at: string;
  last_updated: string | null;
}

interface SupplierRanking {
  supplier_id: number;
  supplier_name: string;
  abn_acn: string;
  total_offered_price: number | null;
  average_score: number | null;
  evaluator_count: number;
  completed_evaluations: number;
  mandatory_pass_rate: number | null;
  compliance_status: string;
  ranking: number;
  vfm_score: number | null;
}

interface EvaluatorSummary {
  evaluator_id: number;
  evaluator_name: string;
  evaluator_email: string;
  total_assignments: number;
  completed_evaluations: number;
  in_progress_evaluations: number;
  completion_rate: number;
  average_score_given: number | null;
  last_activity: string | null;
}

interface CriterionAnalysis {
  criterion_id: number;
  criterion_name: string;
  criterion_type: string;
  weight_percentage: number | null;
  average_score: number | null;
  score_distribution: { [key: string]: number };
  supplier_scores: { [key: number]: number | null };
}

interface MandatoryRequirementAnalysis {
  requirement_id: number;
  requirement_description: string;
  total_evaluations: number;
  pass_count: number;
  fail_count: number;
  pass_rate: number;
  supplier_results: Array<{
    supplier_id: number;
    supplier_name: string;
    status: 'Pass' | 'Fail' | 'Not Evaluated';
    comments: string | null;
  }>;
}

interface ComplianceOverview {
  total_suppliers: number;
  compliant_suppliers: number;
  non_compliant_suppliers: number;
  not_checked_suppliers: number;
  compliance_rate: number;
  criteria_details: Array<{
    criterion_id: number;
    description: string;
    pass_count: number;
    fail_count: number;
    not_checked: number;
    pass_rate: number;
  }>;
  supplier_compliance_details: Array<{
    supplier_id: number;
    supplier_name: string;
    status: string;
    details: Array<{
      criterion: string;
      status: string;
      comments: string | null;
      checked_at: string | null;
    }>;
  }>;
}

const styles = {
    mainContainer: {
      minHeight: '100vh',
      background: 'var(--background-brand-gradient)',
      fontFamily: 'var(--font-family-primary)',
      color: 'var(--color-text-primary)',
      padding: 'var(--spacing-lg)'
    },
    contentWrapper: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    pageTitle: {
      fontSize: 'var(--font-size-headline-large)',
      fontWeight: 'var(--font-weight-headline)',
      color: 'var(--color-text-on-gradient)',
      marginBottom: 'var(--spacing-xl)',
      textAlign: 'center' as 'center'
    },
    card: {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      padding: 'var(--spacing-xl)',
      borderRadius: 'var(--border-radius-lg)',
      boxShadow: 'var(--shadow-lg)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      marginBottom: 'var(--spacing-xl)'
    },
    button: {
      padding: 'var(--spacing-sm) var(--spacing-md)',
      background: 'var(--color-primary)',
      color: 'white',
      border: 'none',
      borderRadius: 'var(--border-radius-md)',
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-medium)',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 'var(--spacing-xs)'
    },
    buttonSecondary: {
        background: 'var(--color-surface-variant)',
        color: 'var(--color-text-primary)',
        border: '2px solid var(--color-primary)',
    },
    dropdownItem: {
        width: '100%',
        padding: '0.75rem 1rem',
        background: 'transparent',
        border: 'none',
        textAlign: 'left' as 'left',
        cursor: 'pointer',
        fontSize: '0.9rem',
        color: '#374151',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        transition: 'background-color 0.2s ease',
        borderRadius: '0',
        justifyContent: 'flex-start'
    }
};

const handleButtonHover = (e: React.MouseEvent<HTMLButtonElement>, enter: boolean) => {
    e.currentTarget.style.transform = enter ? 'translateY(-2px)' : 'translateY(0)';
    e.currentTarget.style.boxShadow = enter ? '0 10px 15px -3px rgba(0, 0, 0, 0.1)' : 'none';
};

const ReportsDashboard: React.FC<ReportsDashboardProps> = ({ 
  processId, 
  processName, 
  userRole, 
  onBack 
}) => {
  const [currentView, setCurrentView] = useState<'overview' | 'rankings' | 'evaluators' | 'criteria' | 'compliance' | 'mandatory' | 'executive' | 'audit-report'>('overview');
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null);
  const [showExportDropdown, setShowExportDropdown] = useState(false);

  // Data states
  const [processSummary, setProcessSummary] = useState<ProcessSummaryReport | null>(null);
  const [supplierRankings, setSupplierRankings] = useState<SupplierRanking[]>([]);
  const [priceQualityAnalysis, setPriceQualityAnalysis] = useState<any>(null);
  const [evaluatorSummaries, setEvaluatorSummaries] = useState<EvaluatorSummary[]>([]);
  const [criterionAnalysis, setCriterionAnalysis] = useState<CriterionAnalysis[]>([]);
  const [complianceOverview, setComplianceOverview] = useState<ComplianceOverview | null>(null);
  const [mandatoryAnalysis, setMandatoryAnalysis] = useState<MandatoryRequirementAnalysis[]>([]);


  const showNotification = useCallback((type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  }, []);

  // Helper functions
  const getStatusBadgeColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'compliant': return '#10b981';
      case 'non-compliant': return '#ef4444';
      case 'completed': return '#10b981';
      case 'in progress': return '#f59e0b';
      case 'not started': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getCompletionColor = (percentage: number) => {
    if (percentage >= 80) return '#10b981';
    if (percentage >= 60) return '#f59e0b';
    return '#ef4444';
  };

  // Methodology-specific helper functions
  const getRankingMethodologyDisplayName = (methodology: string) => {
    switch (methodology) {
      case 'vfm':
        return 'Value for Money (VFM)';
      case 'price_quality':
        return 'Price/Quality Strategic Analysis';
      default:
        return 'Unknown Ranking Methodology';
    }
  };

  const getScoringScaleDisplayName = (scale: string) => {
    switch (scale) {
      case '0-5':
        return '0-5 Point Scale';
      case '0-10':
        return '0-10 Point Scale';
      case 'requirements_based':
        return 'Requirements-Based Normalisation';
      default:
        return 'Unknown Scoring Scale';
    }
  };

  const getScoreColumnHeader = (rankingMethodology: string, scoringScale: string) => {
    if (rankingMethodology === 'vfm') {
      return 'VFM Score';
    } else if (rankingMethodology === 'price_quality') {
      return 'Quality Score';
    }
    return 'Score';
  };

  const getAnalysisTitle = (rankingMethodology: string, scoringScale: string) => {
    const scoringName = getScoringScaleDisplayName(scoringScale);
    const rankingName = getRankingMethodologyDisplayName(rankingMethodology);
    return `${rankingName} (${scoringName})`;
  };

  // Load process summary on component mount
  useEffect(() => {
    loadProcessSummary();
  }, [processId]);

  // Load data based on current view
  useEffect(() => {
  switch (currentView) {
    case 'executive':
      // Executive dashboard needs all data
      loadSupplierRankings();
      loadEvaluatorSummaries();
      loadComplianceOverview();
      break;
    case 'rankings':
      loadSupplierRankings();
      break;
    case 'evaluators':
      loadEvaluatorSummaries();
      break;
    case 'criteria':
      loadCriterionAnalysis();
      break;
    case 'compliance':
      loadComplianceOverview();
      break;
    case 'mandatory':
      loadMandatoryAnalysis();
      break;
  }
}, [currentView, processId]);

  const loadProcessSummary = async () => {
  setIsLoading(true);
  try {
  const token = Cookies.get('access_token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const response = await axios.get(`${API_URL}/api/reports/process/${processId}/summary`, authHeaders);
  setProcessSummary(response.data);

  try {
    const mandatoryResponse = await axios.get(`${API_URL}/api/reports/process/${processId}/mandatory-analysis`, authHeaders);
    setMandatoryAnalysis(mandatoryResponse.data);
  } catch (mandatoryError) {
  }
} catch (error: any) {
  console.error('Error loading process summary:', error);
  showNotification('error', 'Failed to load process summary');
} finally {
  setIsLoading(false);
}
};

  const loadSupplierRankings = async () => {
    setIsLoading(true);
    try {
  const token = Cookies.get('access_token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const response = await axios.get(`${API_URL}/api/reports/process/${processId}/supplier-rankings`, authHeaders);
  setSupplierRankings(response.data);

  // Also load Price/Quality analysis if that methodology is selected
  if (processSummary?.evaluation_methodology === 'price_quality') {
    try {
      const priceQualityResponse = await axios.get(`${API_URL}/api/reports/process/${processId}/price-quality-analysis`, authHeaders);
      setPriceQualityAnalysis(priceQualityResponse.data);
    } catch (priceQualityError) {
      console.error('Error loading price/quality analysis:', priceQualityError);
    }
  }
} catch (error: any) {
  console.error('Error loading supplier rankings:', error);
  showNotification('error', 'Failed to load supplier rankings');
} finally {
  setIsLoading(false);
}
  };

  const loadEvaluatorSummaries = async () => {
    setIsLoading(true);
    try {
  const token = Cookies.get('access_token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const response = await axios.get(`${API_URL}/api/reports/process/${processId}/evaluator-summary`, authHeaders);
  setEvaluatorSummaries(response.data);
} catch (error: any) {
  console.error('Error loading evaluator summaries:', error);
  showNotification('error', 'Failed to load evaluator summaries');
} finally {
  setIsLoading(false);
}
  };

  const loadCriterionAnalysis = async () => {
    setIsLoading(true);
    try {
      const token = Cookies.get('access_token');
      const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API_URL}/api/reports/process/${processId}/criterion-analysis`, authHeaders);
      setCriterionAnalysis(response.data);
    } catch (error: any) {
      console.error('Error loading criterion analysis:', error);
      console.error('Error details:', error.response?.data || error.message);
      showNotification('error', 'Failed to load criterion analysis');
      setCriterionAnalysis([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadComplianceOverview = async () => {
    setIsLoading(true);
    try {
  const token = Cookies.get('access_token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const response = await axios.get(`${API_URL}/api/reports/process/${processId}/compliance-overview`, authHeaders);
  setComplianceOverview(response.data);
} catch (error: any) {
  console.error('Error loading compliance overview:', error);
  showNotification('error', 'Failed to load compliance overview');
} finally {
  setIsLoading(false);
}
  };

  const exportData = async (type: 'csv' | 'json' | 'xlsx' | 'pdf') => {
    try {
      let data: any = {};
      
      switch (currentView) {
        case 'rankings':
          data = supplierRankings;
          break;
        case 'evaluators':
          data = evaluatorSummaries;
          break;
        case 'criteria':
          data = criterionAnalysis;
          break;
        case 'compliance':
          data = complianceOverview;
          break;
        case 'mandatory':
          data = mandatoryAnalysis;
          break;
        default:
          data = processSummary;
      }

      const filename = `${processName}_${currentView}_report_${new Date().toISOString().split('T')[0]}`;

      if (type === 'json') {
        const jsonData = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonData], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${filename}.json`;
        a.click();
        URL.revokeObjectURL(url);
      } else if (type === 'csv') {
        const csvContent = convertToCSV(data);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${filename}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      } else if (type === 'xlsx') {
        const htmlContent = convertToHTMLTable(data);
        const blob = new Blob([htmlContent], { type: 'application/vnd.ms-excel' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${filename}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      } else if (type === 'pdf') {
        generatePDFReport(data, filename);
      }
      
      showNotification('success', `${type.toUpperCase()} export completed`);
    } catch (error) {
      console.error('Export error:', error);
      showNotification('error', 'Export failed');
    }
  };

  const convertToCSV = (data: any): string => {
    if (Array.isArray(data)) {
      if (data.length === 0) return '';
      const headers = Object.keys(data[0]);
      const csvRows = [headers.join(',')];
      for (const row of data) {
        const values = headers.map(header => {
          const value = row[header];
          return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
        });
        csvRows.push(values.join(','));
      }
      return csvRows.join('\n');
    } else {
      // Convert object to CSV
      const entries = Object.entries(data);
      const csvRows = ['Property,Value'];
      for (const [key, value] of entries) {
        const csvValue = typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
        csvRows.push(`${key},${csvValue}`);
      }
      return csvRows.join('\n');
    }
  };

  const convertToHTMLTable = (data: any): string => {
    const htmlStart = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    table { border-collapse: collapse; width: 100%; }
    th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
    th { background-color: #f2f2f2; font-weight: bold; }
  </style>
</head>
<body>
  <h1>${processName} - ${currentView.charAt(0).toUpperCase() + currentView.slice(1)} Report</h1>
  <table>`;

    if (Array.isArray(data) && data.length > 0) {
      const headers = Object.keys(data[0]);
      const headerRow = `<tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>`;
      const dataRows = data.map(row => 
        `<tr>${headers.map(h => `<td>${row[h] || ''}</td>`).join('')}</tr>`
      ).join('');
      return `${htmlStart}${headerRow}${dataRows}</table></body></html>`;
    } else {
      const entries = Object.entries(data);
      const rows = entries.map(([key, value]) => 
        `<tr><td><strong>${key}</strong></td><td>${value}</td></tr>`
      ).join('');
      return `${htmlStart}<tr><th>Property</th><th>Value</th></tr>${rows}</table></body></html>`;
    }
  };

  const generatePDFReport = (data: any, filename: string) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const htmlContent = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${processName} - ${currentView.charAt(0).toUpperCase() + currentView.slice(1)} Report</title>
  <style>
    @media print {
      body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
      .no-print { display: none; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
      th, td { border: 1px solid #333; padding: 8px; text-align: left; font-size: 11px; }
      th { background-color: #f0f0f0; font-weight: bold; }
      .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
    }
    body { font-family: Arial, sans-serif; margin: 20px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
    th, td { border: 1px solid #333; padding: 8px; text-align: left; }
    th { background-color: #f0f0f0; font-weight: bold; }
    .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
    .print-btn { margin: 20px 0; padding: 10px 20px; background: #4472C4; color: white; border: none; cursor: pointer; }
  </style>
</head>
<body>
  <button class="print-btn no-print" onclick="window.print()">🖨️ Print/Save as PDF</button>
  
  <div class="header">
    <h1>${processName}</h1>
    <h2>${currentView.charAt(0).toUpperCase() + currentView.slice(1)} Report</h2>
    <p>Generated: ${new Date().toLocaleDateString('en-AU')}</p>
  </div>
  
  ${convertToHTMLTable(data)}
</body>
</html>`;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
  };

        const renderOverview = () => {
  if (!processSummary) return <div>Loading process summary...</div>;

  const complianceData = [
      { name: 'Compliant', value: processSummary.compliant_suppliers },
      { name: 'Non-Compliant', value: processSummary.total_suppliers - processSummary.compliant_suppliers },
  ];
  const COLORS = ['#10b981', '#ef4444'];
  
  const progressData = [
      { name: 'Completion', value: processSummary.completion_percentage, fill: '#2563eb' }
  ];

  // Calculate mandatory requirements data
  const mandatoryData = mandatoryAnalysis.length > 0 ? (() => {
    const totalRequirements = mandatoryAnalysis.length;
    const avgPassRate = mandatoryAnalysis.reduce((sum, req) => sum + req.pass_rate, 0) / totalRequirements;
    return [
      { name: 'Met', value: avgPassRate },
      { name: 'Not Met', value: 100 - avgPassRate }
    ];
  })() : [{ name: 'No Data', value: 100 }];

  // FIX: Check if the donut chart will only have one segment to avoid the gap
  const isFullCircle = processSummary.compliant_suppliers === 0 || processSummary.compliant_suppliers === processSummary.total_suppliers;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '2rem', alignItems: 'stretch' }}>
      {/* First Column: Key Metrics */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          <div style={{ background: '#f9fafb', padding: '1.5rem', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#374151', fontSize: '1rem', fontWeight: 'bold' }}>PROCESS STATUS</h3>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1e293b' }}>{processSummary.status}</div>
              <div style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '0.5rem' }}>ITO: {processSummary.ito_reference}</div>
          </div>
          <div style={{ background: '#f9fafb', padding: '1.5rem', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#374151', fontSize: '1rem', fontWeight: 'bold' }}>AVERAGE SCORE</h3>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3b82f6' }}>
              {processSummary.average_process_score ? `${processSummary.average_process_score.toFixed(2)}/100` : 'N/A'}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '0.5rem' }}>Across all evaluations</div>
          </div>
      </div>

      {/* Second Column: Progress Gauge */}
      <div style={{ background: '#f9fafb', padding: '1.5rem', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#374151', fontSize: '1rem', fontWeight: 'bold', textAlign: 'center' }}>EVALUATION PROGRESS</h3>
          <ResponsiveContainer width="100%" height={200}>
              <RadialBarChart 
                  innerRadius="70%" 
                  outerRadius="100%" 
                  data={progressData} 
                  startAngle={180} 
                  endAngle={0}
                  barSize={30}
              >
                  <RadialBar
                      background
                      dataKey="value"
                  />
                  <text x="50%" y="55%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '2.5rem', fontWeight: 'bold', fill: '#1e293b' }}>
                      {`${processSummary.completion_percentage}%`}
                  </text>
                  <text x="50%" y="70%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '0.9rem', fill: '#6b7280' }}>
                      Completed
                  </text>
              </RadialBarChart>
          </ResponsiveContainer>
           <div style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '0.5rem', textAlign: 'center' }}>
            {processSummary.completed_evaluations} of {processSummary.total_evaluations_possible} evaluations
          </div>
      </div>

      {/* Third Column: Compliance Donut */}
      <div style={{ background: '#f9fafb', padding: '1.5rem', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
           <h3 style={{ margin: '0 0 1rem 0', color: '#374151', fontSize: '1rem', fontWeight: 'bold', textAlign: 'center' }}>SUPPLIER COMPLIANCE</h3>
          <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                  <Pie
                      data={complianceData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      fill="#8884d8"
                      paddingAngle={isFullCircle ? 0 : 5}
                      dataKey="value"
                  >
                      {complianceData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                  </Pie>
                   <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '1.5rem', fontWeight: 'bold', fill: '#1e293b' }}>
                      {`${processSummary.compliant_suppliers}/${processSummary.total_suppliers}`}
                  </text>
              </PieChart>
          </ResponsiveContainer>
          <div style={{textAlign: 'center', fontSize: '0.9rem', color: '#6b7280'}}>
              <span style={{color: '#10b981', fontWeight: 'bold'}}>● Compliant</span> | <span style={{color: '#ef4444', fontWeight: 'bold'}}>● Non-Compliant</span>
          </div>
      </div>

      {/* Fourth Column: Mandatory Requirements */}
      <div style={{ background: '#f9fafb', padding: '1.5rem', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
           <h3 style={{ margin: '0 0 1rem 0', color: '#374151', fontSize: '1rem', fontWeight: 'bold', textAlign: 'center' }}>MANDATORY REQUIREMENTS</h3>
          {mandatoryAnalysis.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                      <Pie
                          data={mandatoryData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          fill="#8884d8"
                          paddingAngle={5}
                          dataKey="value"
                      >
                          <Cell fill="#10b981" />
                          <Cell fill="#ef4444" />
                      </Pie>
                       <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '1.5rem', fontWeight: 'bold', fill: '#1e293b' }}>
                          {`${mandatoryData[0].value.toFixed(1)}%`}
                      </text>
                  </PieChart>
              </ResponsiveContainer>
              <div style={{textAlign: 'center', fontSize: '0.9rem', color: '#6b7280'}}>
                  <span style={{color: '#10b981', fontWeight: 'bold'}}>● Met</span> | <span style={{color: '#ef4444', fontWeight: 'bold'}}>● Not Met</span>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', color: '#6b7280', fontSize: '0.9rem' }}>
              No mandatory requirements defined
            </div>
          )}
      </div>
    </div>
  );
};


      const renderSupplierRankings = () => {
  if (supplierRankings.length === 0) {
    return <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No supplier evaluation data available yet.</div>;
  }

  const chartData = supplierRankings.map(s => ({
      x: s.average_score,
      y: s.total_offered_price,
      z: s.vfm_score,
      name: s.supplier_name,
      ranking: s.ranking,
      // Create a symbol based on ranking
      symbol: s.ranking === 1 ? '🏆' : s.ranking === 2 ? '🥈' : s.ranking === 3 ? '🥉' : s.ranking.toString()
  }));

  return (
    <div>
      <div style={{ background: '#f9fafb', padding: '1.5rem', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '2rem' }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#1e293b' }}>{getAnalysisTitle(processSummary?.evaluation_methodology || 'vfm', processSummary?.score_scale || '0-5')}</h3>
          
          {/* VFM Scatter Chart - only show for VFM methodology */}
          {processSummary?.evaluation_methodology === 'vfm' && (
            <ResponsiveContainer width="100%" height={400}>
      <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
          <CartesianGrid />
          <XAxis type="number" dataKey="z" name={getScoreColumnHeader(processSummary?.evaluation_methodology || 'vfm', processSummary?.score_scale || '0-5')} domain={['dataMin - 10', 'dataMax + 10']} />
          <YAxis type="number" dataKey="y" name="Price" unit="$" tickFormatter={(value) => new Intl.NumberFormat('en-AU').format(value)} />
          <Tooltip cursor={{ strokeDasharray: '3 3' }} content={({ active, payload }) => {
              if (active && payload && payload.length) {
                  const data = payload[0].payload;
                return (
                    <div style={{ background: 'white', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'}}>
                        <p style={{margin: 0, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                            <span style={{ fontSize: '1.2rem' }}>
                                {data.ranking === 1 ? '🏆' : data.ranking === 2 ? '🥈' : data.ranking === 3 ? '🥉' : `#${data.ranking}`}
                            </span>
                            {data.name}
                        </p>
                        <p style={{margin: '0.5rem 0 0 0', color: '#10b981'}}>Avg Score: {data.x}/100</p>
                        <p style={{margin: '0.5rem 0 0 0', color: '#3b82f6'}}>Price: ${new Intl.NumberFormat('en-AU').format(data.y)}</p>
                        <p style={{margin: '0.5rem 0 0 0', color: '#8b5cf6'}}>VFM Score: {data.z?.toFixed(2)}</p>
                    </div>
                );
            }
            return null;
        }}/>
        <Legend />
        <Scatter name="Suppliers" data={chartData} fill="transparent">
            <LabelList dataKey="symbol" position="center" style={{ 
                fill: '#1e293b', 
                fontWeight: 'bold', 
                fontSize: '16px',
                textAnchor: 'middle',
                dominantBaseline: 'middle'
            }} />
        </Scatter>
    </ScatterChart>
</ResponsiveContainer>
          )}

          {/* Price/Quality Analysis - only show for price_quality methodology */}
          {processSummary?.evaluation_methodology === 'price_quality' && priceQualityAnalysis && (
            <div>
              {priceQualityAnalysis.analysis_available ? (
                <div>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h4 style={{ color: '#374151', marginBottom: '0.5rem' }}>Baseline Supplier</h4>
                    <p style={{ color: '#6b7280', margin: 0 }}>
                      <strong>{priceQualityAnalysis.baseline_supplier?.supplier_name}</strong> - 
                      ${priceQualityAnalysis.baseline_supplier?.cost?.toLocaleString('en-AU')} 
                      (Quality: {priceQualityAnalysis.baseline_supplier?.quality?.toFixed(1)})
                    </p>
                  </div>

                  {/* Cost vs Quality Scatter Plot */}
                  <div style={{ marginBottom: '2rem', background: 'white', padding: '1.5rem', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                    <h4 style={{ color: '#374151', marginBottom: '1rem', textAlign: 'center' }}>Cost vs Quality Strategic Position</h4>
                    <ResponsiveContainer width="100%" height={400}>
                      <ScatterChart margin={{ top: 20, right: 80, bottom: 60, left: 80 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis 
                          dataKey="cost_difference" 
                          type="number" 
                          domain={['dataMin - 100000', 'dataMax + 100000']}
                          tickFormatter={(value) => `$${(value/1000).toFixed(0)}K`}
                          stroke="#6b7280"
                          fontSize={12}
                        />
                        <YAxis 
                          dataKey="quality_difference" 
                          type="number" 
                          domain={['dataMin - 5', 'dataMax + 5']}
                          tickFormatter={(value) => `${value.toFixed(1)}`}
                          stroke="#6b7280"
                          fontSize={12}
                        />
                        <Tooltip 
                          formatter={(value, name) => {
                            if (name === 'cost_difference') return [`$${Number(value).toLocaleString('en-AU')}`, 'Cost Difference'];
                            if (name === 'quality_difference') return [Number(value).toFixed(1), 'Quality Difference'];
                            if (name === 'quality_premium') return [`$${Number(value).toLocaleString('en-AU')}/point`, 'Quality Premium'];
                            return [value, name];
                          }}
                          labelFormatter={(label, payload) => payload && payload.length > 0 ? `Supplier: ${payload[0].payload.supplier_name}` : ''}
                          contentStyle={{ 
                            background: 'white', 
                            border: '1px solid #e2e8f0', 
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                          }}
                        />
                        <ReferenceLine x={0} stroke="#94a3b8" strokeDasharray="2 2" />
                        <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="2 2" />
                        
                        {/* Different scatter plots for each category */}
                        <Scatter 
                          name="Excellent Value" 
                          data={priceQualityAnalysis.quality_premiums?.filter((item: any) => item.premium_category === 'excellent') || []}
                          fill="#10b981"
                        />
                        <Scatter 
                          name="Good Value" 
                          data={priceQualityAnalysis.quality_premiums?.filter((item: any) => item.premium_category === 'good') || []}
                          fill="#3b82f6"
                        />
                        <Scatter 
                          name="Expensive" 
                          data={priceQualityAnalysis.quality_premiums?.filter((item: any) => item.premium_category === 'expensive') || []}
                          fill="#f59e0b"
                        />
                        <Scatter 
                          name="Very Expensive" 
                          data={priceQualityAnalysis.quality_premiums?.filter((item: any) => item.premium_category === 'very_expensive') || []}
                          fill="#ef4444"
                        />
                        <Scatter 
                          name="Exceptional" 
                          data={priceQualityAnalysis.quality_premiums?.filter((item: any) => item.premium_category === 'exceptional') || []}
                          fill="#8b5cf6"
                        />
                        <Scatter 
                          name="Baseline" 
                          data={priceQualityAnalysis.quality_premiums?.filter((item: any) => item.is_baseline) || []}
                          fill="#6b7280"
                        />
                        
                        <Legend 
                          verticalAlign="bottom" 
                          height={36}
                          iconType="circle"
                          wrapperStyle={{ fontSize: '12px' }}
                        />
                      </ScatterChart>
                    </ResponsiveContainer>
                    
                    {/* Chart interpretation guide */}
                    <div style={{ 
                      marginTop: '1rem', 
                      padding: '1rem', 
                      background: '#f8fafc', 
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      color: '#6b7280'
                    }}>
                      <p style={{ margin: '0 0 0.5rem 0', fontWeight: 'bold' }}>Chart Guide:</p>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem' }}>
                        <span>• <strong>Top Right:</strong> Higher cost, higher quality</span>
                        <span>• <strong>Top Left:</strong> Lower cost, higher quality (Best value)</span>
                        <span>• <strong>Bottom Right:</strong> Higher cost, lower quality (Poor value)</span>
                        <span>• <strong>Bottom Left:</strong> Lower cost, lower quality</span>
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ overflowX: 'auto', marginTop: '1rem' }}>
                    <table style={{ 
                      width: '100%', 
                      borderCollapse: 'separate',
                      borderSpacing: '0',
                      background: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                      border: '1px solid #e5e7eb'
                    }}>
                      <thead>
                        <tr style={{ 
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white'
                        }}>
                          <th style={{ 
                            padding: '1.25rem 1rem', 
                            textAlign: 'left', 
                            fontWeight: 'bold', 
                            fontSize: '0.9rem',
                            borderTopLeftRadius: '12px'
                          }}>
                            Supplier
                          </th>
                          <th style={{ 
                            padding: '1.25rem 1rem', 
                            textAlign: 'right', 
                            fontWeight: 'bold',
                            fontSize: '0.9rem'
                          }}>
                            Cost Difference
                          </th>
                          <th style={{ 
                            padding: '1.25rem 1rem', 
                            textAlign: 'center', 
                            fontWeight: 'bold',
                            fontSize: '0.9rem'
                          }}>
                            Quality Difference
                          </th>
                          <th style={{ 
                            padding: '1.25rem 1rem', 
                            textAlign: 'right', 
                            fontWeight: 'bold',
                            fontSize: '0.9rem'
                          }}>
                            Quality Premium
                          </th>
                          <th style={{ 
                            padding: '1.25rem 1rem', 
                            textAlign: 'center', 
                            fontWeight: 'bold',
                            fontSize: '0.9rem'
                          }}>
                            Category
                          </th>
                          <th style={{ 
                            padding: '1.25rem 1rem', 
                            textAlign: 'left', 
                            fontWeight: 'bold',
                            fontSize: '0.9rem',
                            borderTopRightRadius: '12px'
                          }}>
                            Recommendation
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {priceQualityAnalysis.quality_premiums?.map((item: any, index: number) => (
                          <tr 
                            key={item.supplier_id} 
                            style={{ 
                              background: index % 2 === 0 ? '#ffffff' : '#f8fafc',
                              borderBottom: index === priceQualityAnalysis.quality_premiums.length - 1 ? 'none' : '1px solid #f1f5f9',
                              transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f0f9ff';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f8fafc';
                            }}
                          >
                            <td style={{ 
                              padding: '1.25rem 1rem', 
                              fontWeight: item.is_baseline ? 'bold' : 'normal',
                              color: item.is_baseline ? '#1e293b' : '#374151'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                {item.is_baseline && (
                                  <span style={{ 
                                    width: '8px', 
                                    height: '8px', 
                                    background: '#3b82f6', 
                                    borderRadius: '50%',
                                    flexShrink: 0
                                  }}></span>
                                )}
                                <span>{item.supplier_name} {item.is_baseline && '(Baseline)'}</span>
                              </div>
                            </td>
                            <td style={{ 
                              padding: '1.25rem 1rem', 
                              textAlign: 'right',
                              fontWeight: '600',
                              color: item.cost_difference >= 0 ? '#ef4444' : '#10b981'
                            }}>
                              {item.cost_difference >= 0 ? '+' : ''}${item.cost_difference?.toLocaleString('en-AU')}
                            </td>
                            <td style={{ 
                              padding: '1.25rem 1rem', 
                              textAlign: 'center',
                              fontWeight: '600',
                              color: item.quality_difference >= 0 ? '#10b981' : '#ef4444'
                            }}>
                              {item.quality_difference >= 0 ? '+' : ''}{item.quality_difference?.toFixed(1)}
                            </td>
                            <td style={{ 
                              padding: '1.25rem 1rem', 
                              textAlign: 'right',
                              fontWeight: '500',
                              color: '#374151'
                            }}>
                              {item.quality_premium ? `$${item.quality_premium.toLocaleString('en-AU')}/point` : 'N/A'}
                            </td>
                            <td style={{ padding: '1.25rem 1rem', textAlign: 'center' }}>
                              <span style={{ 
                                background: item.premium_category === 'excellent' ? '#10b981' : 
                                           item.premium_category === 'good' ? '#3b82f6' :
                                           item.premium_category === 'expensive' ? '#f59e0b' :
                                           item.premium_category === 'very_expensive' ? '#ef4444' :
                                           item.premium_category === 'exceptional' ? '#8b5cf6' :
                                           item.premium_category === 'poor_value' ? '#ef4444' : '#6b7280',
                                color: 'white', 
                                padding: '0.375rem 0.875rem', 
                                borderRadius: '9999px', 
                                fontSize: '0.8rem', 
                                fontWeight: 'bold',
                                display: 'inline-block',
                                minWidth: '90px',
                                textAlign: 'center'
                              }}>
                                {item.premium_category.replace('_', ' ').toUpperCase()}
                              </span>
                            </td>
                            <td style={{ 
                              padding: '1.25rem 1rem', 
                              fontSize: '0.9rem',
                              lineHeight: '1.4',
                              color: '#6b7280',
                              maxWidth: '250px'
                            }}>
                              {item.recommendation}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>
                  {priceQualityAnalysis.message || 'Price/Quality analysis not available yet.'}
                </p>
              )}
            </div>
          )}
      </div>

        {/* Modern Card-Based Supplier Rankings */}
        <div style={{ display: 'grid', gap: '1rem', marginTop: '1.5rem' }}>
          {supplierRankings.map((supplier) => (
            <div key={supplier.supplier_id} style={{ 
              background: 'white', 
              borderRadius: '16px', 
              padding: '1.5rem',
              boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
              border: '1px solid #e5e7eb',
              transition: 'all 0.2s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.05)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}>
              
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem' }}>
                {/* Rank and Supplier Info */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    width: '50px', 
                    height: '50px',
                    borderRadius: '50%',
                    background: supplier.ranking <= 3 ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#f3f4f6',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: supplier.ranking <= 3 ? 'white' : '#6b7280'
                  }}>
                    {supplier.ranking === 1 ? '🏆' : supplier.ranking === 2 ? '🥈' : supplier.ranking === 3 ? '🥉' : supplier.ranking}
                  </div>
                  
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold', color: '#1e293b' }}>
                      {supplier.supplier_name}
                    </h3>
                    <p style={{ margin: '0.25rem 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>
                      {supplier.abn_acn}
                    </p>
                  </div>
                </div>

                {/* Compliance Badge */}
                <span style={{ 
                  background: getStatusBadgeColor(supplier.compliance_status), 
                  color: 'white', 
                  padding: '0.5rem 1rem', 
                  borderRadius: '20px', 
                  fontSize: '0.8rem', 
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {supplier.compliance_status}
                </span>
              </div>

              {/* Metrics Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem' }}>
                
                {/* Average Score */}
                <div style={{ textAlign: 'center', padding: '1rem', background: '#f8fafc', borderRadius: '12px' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>
                    Average Score
                  </div>
                  <div style={{ 
                    fontSize: '1.5rem', 
                    fontWeight: 'bold', 
                    color: supplier.average_score !== null ? 
                      (supplier.average_score >= 75 ? '#10b981' : supplier.average_score >= 50 ? '#f59e0b' : '#ef4444') : 
                      '#6b7280' 
                  }}>
                    {supplier.average_score !== null ? `${supplier.average_score.toFixed(1)}` : 'N/A'}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    out of 100
                  </div>
                </div>

                {/* Price */}
                <div style={{ textAlign: 'center', padding: '1rem', background: '#f8fafc', borderRadius: '12px' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>
                    Total Price
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1e293b' }}>
                    {supplier.total_offered_price ? `$${supplier.total_offered_price.toLocaleString('en-AU')}` : 'N/A'}
                  </div>
                </div>

                {/* Quality/VFM Score */}
                <div style={{ textAlign: 'center', padding: '1rem', background: '#f8fafc', borderRadius: '12px' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>
                    {getScoreColumnHeader(processSummary?.evaluation_methodology || 'vfm', processSummary?.score_scale || '0-5')}
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#8b5cf6' }}>
                    {supplier.vfm_score ? supplier.vfm_score.toFixed(2) : 'N/A'}
                  </div>
                </div>

                {/* Evaluations Progress */}
                <div style={{ textAlign: 'center', padding: '1rem', background: '#f8fafc', borderRadius: '12px' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>
                    Evaluations
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1e293b' }}>
                    {supplier.completed_evaluations}/{supplier.evaluator_count}
                  </div>
                  <div style={{ 
                    width: '100%', 
                    height: '4px', 
                    background: '#e5e7eb', 
                    borderRadius: '2px', 
                    marginTop: '0.5rem',
                    overflow: 'hidden'
                  }}>
                    <div style={{ 
                      width: `${supplier.evaluator_count > 0 ? (supplier.completed_evaluations / supplier.evaluator_count) * 100 : 0}%`, 
                      height: '100%', 
                      background: supplier.completed_evaluations === supplier.evaluator_count ? '#10b981' : '#3b82f6',
                      transition: 'width 0.3s ease'
                    }}></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };



  const renderEvaluatorSummaries = () => {
    if (evaluatorSummaries.length === 0) {
      return <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No evaluators assigned to this process yet.</div>;
    }

    return (
      <div style={{ display: 'grid', gap: '1rem' }}>
        {evaluatorSummaries.map((evaluator) => (
          <div key={evaluator.evaluator_id} style={{ 
            background: 'white', 
            padding: '1.5rem', 
            borderRadius: '12px', 
            boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
              <div>
                <h4 style={{ margin: 0, color: '#1e293b' }}>{evaluator.evaluator_name}</h4>
                <p style={{ margin: '0.25rem 0 0 0', color: '#6b7280', fontSize: '0.9rem' }}>{evaluator.evaluator_email}</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ 
                  fontSize: '1.2rem', 
                  fontWeight: 'bold', 
                  color: getCompletionColor(evaluator.completion_rate) 
                }}>
                  {evaluator.completion_rate}%
                </div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Complete</div>
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '1rem' }}>
              <div>
                <div style={{ color: '#6b7280', fontSize: '0.8rem', fontWeight: 'bold' }}>ASSIGNMENTS</div>
                <div style={{ color: '#374151', fontSize: '1.1rem', fontWeight: 'bold' }}>{evaluator.total_assignments}</div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '0.8rem', fontWeight: 'bold' }}>COMPLETED</div>
                <div style={{ color: '#10b981', fontSize: '1.1rem', fontWeight: 'bold' }}>{evaluator.completed_evaluations}</div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '0.8rem', fontWeight: 'bold' }}>IN PROGRESS</div>
                <div style={{ color: '#f59e0b', fontSize: '1.1rem', fontWeight: 'bold' }}>{evaluator.in_progress_evaluations}</div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '0.8rem', fontWeight: 'bold' }}>AVG SCORE</div>
                <div style={{ color: '#3b82f6', fontSize: '1.1rem', fontWeight: 'bold' }}>
                  {evaluator.average_score_given !== null ? `${evaluator.average_score_given}/100` : 'N/A'}
                </div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '0.8rem', fontWeight: 'bold' }}>LAST ACTIVITY</div>
                <div style={{ color: '#374151', fontSize: '0.9rem' }}>
                  {evaluator.last_activity ? new Date(evaluator.last_activity).toLocaleDateString('en-AU') : 'None'}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

    const renderCriterionAnalysis = () => {
    
    if (!criterionAnalysis || criterionAnalysis.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
          <p>No criterion analysis data available yet.</p>
          <p style={{ fontSize: '0.8rem', marginTop: '1rem' }}>
            Debug: criterionAnalysis is {criterionAnalysis ? `array with ${criterionAnalysis.length} items` : 'null/undefined'}
          </p>
        </div>
      );
    }

    // Only show scoring criteria (sub-criteria) in the Criteria tab, not mandatory requirements
    const subCriteria = criterionAnalysis.filter((c) => c.criterion_type === 'sub');
    
    if (subCriteria.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
          <p>No scoring criteria analysis data available yet.</p>
          <p style={{ fontSize: '0.8rem', marginTop: '1rem' }}>
            Mandatory requirements are available in the "Mandatory" tab.
          </p>
        </div>
      );
    }
    
    return (
      <div style={{ display: 'grid', gap: '2rem' }}>
        <div style={{ background: '#f9fafb', padding: '1.5rem', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          <h3 style={{ margin: '0 0 1.5rem 0', color: '#1e293b' }}>Evaluation Criteria Performance</h3>
          <div style={{ display: 'grid', gap: '2rem' }}>
            {subCriteria.map((criterion) => {
              const distributionData = Object.entries(criterion.score_distribution)
                  .map(([score, count]) => ({ score: score.split('-')[0], count }))
                  .sort((a, b) => parseInt(a.score) - parseInt(b.score));
              
              const maxCount = Math.max(...distributionData.map(d => d.count), 0);

              return (
                  <div key={criterion.criterion_id} style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '1.5rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                          <h4 style={{ margin: 0, color: '#374151' }}>{criterion.criterion_name}</h4>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>Weight: {criterion.weight_percentage}%</span>
                          <span style={{ background: '#3b82f6', color: 'white', padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '0.9rem', fontWeight: 'bold' }}>
                              Avg: {criterion.average_score?.toFixed(2) || 'N/A'}
                          </span>
                          </div>
                      </div>
                      <ResponsiveContainer width="100%" height={150}>
                          <BarChart data={distributionData} margin={{ top: 20, right: 20, left: -10, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="score" />
                              <YAxis 
                                  allowDecimals={false} 
                                  domain={[0, maxCount > 4 ? maxCount : 4]}
                                  interval={0} 
                              />
                              <Tooltip />
                              <Bar dataKey="count" fill="#8884d8">
                                  <LabelList dataKey="count" position="top" />
                              </Bar>
                          </BarChart>
                      </ResponsiveContainer>
                  </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };


  const renderComplianceOverview = () => {
    if (!complianceOverview) {
      return <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Loading compliance overview...</div>;
    }

    return (
      <div style={{ display: 'grid', gap: '2rem' }}>
        {/* Compliance Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#374151', fontSize: '0.9rem', fontWeight: 'bold' }}>TOTAL SUPPLIERS</h3>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1e293b' }}>{complianceOverview.total_suppliers}</div>
          </div>

          <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#374151', fontSize: '0.9rem', fontWeight: 'bold' }}>COMPLIANT</h3>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>{complianceOverview.compliant_suppliers}</div>
            <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{complianceOverview.compliance_rate}% of total</div>
          </div>

          <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#374151', fontSize: '0.9rem', fontWeight: 'bold' }}>NON-COMPLIANT</h3>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ef4444' }}>{complianceOverview.non_compliant_suppliers}</div>
          </div>

          <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#374151', fontSize: '0.9rem', fontWeight: 'bold' }}>NOT CHECKED</h3>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#6b7280' }}>{complianceOverview.not_checked_suppliers}</div>
          </div>
        </div>

        {/* Compliance Criteria Details */}
        <div style={{ background: 'white', padding: '2rem', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
          <h3 style={{ margin: '0 0 1.5rem 0', color: '#1e293b' }}>Compliance Criteria Performance</h3>
          <div style={{ display: 'grid', gap: '1rem' }}>
            {complianceOverview.criteria_details.map((criterion) => (
              <div key={criterion.criterion_id} style={{ 
                background: '#f8fafc', 
                padding: '1rem', 
                borderRadius: '8px', 
                border: '1px solid #e2e8f0' 
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <h4 style={{ margin: 0, color: '#374151', flex: 1 }}>{criterion.description}</h4>
                  <div style={{ 
                    fontSize: '1.1rem', 
                    fontWeight: 'bold', 
                    color: criterion.pass_rate >= 75 ? '#10b981' : criterion.pass_rate >= 50 ? '#f59e0b' : '#ef4444' 
                  }}>
                    {criterion.pass_rate.toFixed(1)}%
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <span style={{ 
                    background: '#10b981', 
                    color: 'white', 
                    padding: '0.25rem 0.5rem', 
                    borderRadius: '12px', 
                    fontSize: '0.75rem', 
                    fontWeight: 'bold' 
                  }}>
                    Pass: {criterion.pass_count}
                  </span>
                  <span style={{ 
                    background: '#ef4444', 
                    color: 'white', 
                    padding: '0.25rem 0.5rem', 
                    borderRadius: '12px', 
                    fontSize: '0.75rem', 
                    fontWeight: 'bold' 
                  }}>
                    Fail: {criterion.fail_count}
                  </span>
                  {criterion.not_checked > 0 && (
                    <span style={{ 
                      background: '#6b7280', 
                      color: 'white', 
                      padding: '0.25rem 0.5rem', 
                      borderRadius: '12px', 
                      fontSize: '0.75rem', 
                      fontWeight: 'bold' 
                    }}>
                      Not Checked: {criterion.not_checked}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Supplier Compliance Details */}
        <div style={{ background: 'white', padding: '2rem', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
          <h3 style={{ margin: '0 0 1.5rem 0', color: '#1e293b' }}>Supplier Compliance Status</h3>
          <div style={{ display: 'grid', gap: '1rem' }}>
            {complianceOverview.supplier_compliance_details.map((supplier) => (
              <div key={supplier.supplier_id} style={{ 
                background: '#f8fafc', 
                padding: '1rem', 
                borderRadius: '8px', 
                border: `2px solid ${getStatusBadgeColor(supplier.status)}` 
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <h4 style={{ margin: 0, color: '#374151' }}>{supplier.supplier_name}</h4>
                  <span style={{
                    background: getStatusBadgeColor(supplier.status),
                    color: 'white',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}>
                    {supplier.status}
                  </span>
                </div>
                {supplier.details.length > 0 && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <div style={{ color: '#6b7280', fontSize: '0.8rem', marginBottom: '0.25rem' }}>Compliance Details:</div>
                    <div style={{ display: 'grid', gap: '0.25rem' }}>
                      {supplier.details.map((detail, index) => (
                        <div key={index} style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          fontSize: '0.8rem',
                          color: '#374151'
                        }}>
                          <span>{detail.criterion}</span>
                          <span style={{
                            background: detail.status === 'Pass' ? '#10b981' : detail.status === 'Fail' ? '#ef4444' : '#6b7280',
                            color: 'white',
                            padding: '0.125rem 0.5rem',
                            borderRadius: '8px',
                            fontSize: '0.7rem',
                            fontWeight: 'bold'
                          }}>
                            {detail.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderMandatoryAnalysis = () => {
  if (mandatoryAnalysis.length === 0) {
    return <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No mandatory requirements analysis data available yet.</div>;
  }

  return (
    <div style={{ display: 'grid', gap: '2rem' }}>
      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
        {mandatoryAnalysis.map((requirement) => (
          <div key={requirement.requirement_id} style={{ 
            background: 'white', 
            padding: '1.5rem', 
            borderRadius: '12px', 
            boxShadow: '0 4px 6px rgba(0,0,0,0.05)', 
            border: '1px solid #e2e8f0' 
          }}>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#374151', fontSize: '0.9rem', fontWeight: 'bold' }}>
              {requirement.requirement_description}
            </h3>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: requirement.pass_rate >= 75 ? '#10b981' : requirement.pass_rate >= 50 ? '#f59e0b' : '#ef4444' }}>
              {requirement.pass_rate.toFixed(1)}%
            </div>
            <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
              {requirement.pass_count}/{requirement.total_evaluations} passed
            </div>
          </div>
        ))}
      </div>

      {/* Detailed Analysis */}
      {mandatoryAnalysis.map((requirement) => (
        <div key={requirement.requirement_id} style={{ 
          background: 'white', 
          padding: '2rem', 
          borderRadius: '12px', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.05)', 
          border: '1px solid #e2e8f0' 
        }}>
          <h3 style={{ margin: '0 0 1.5rem 0', color: '#1e293b' }}>{requirement.requirement_description}</h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            <div style={{ textAlign: 'center', padding: '1rem', background: '#f0fdf4', borderRadius: '8px' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981' }}>{requirement.pass_count}</div>
              <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Pass</div>
            </div>
            <div style={{ textAlign: 'center', padding: '1rem', background: '#fef2f2', borderRadius: '8px' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ef4444' }}>{requirement.fail_count}</div>
              <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Fail</div>
            </div>
            <div style={{ textAlign: 'center', padding: '1rem', background: '#f8fafc', borderRadius: '8px' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#6b7280' }}>
                {requirement.supplier_results.filter(s => s.status === 'Not Evaluated').length}
              </div>
              <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Not Evaluated</div>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: '#374151', fontWeight: 'bold' }}>Supplier</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', color: '#374151', fontWeight: 'bold' }}>Status</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: '#374151', fontWeight: 'bold' }}>Comments</th>
                </tr>
              </thead>
              <tbody>
                {requirement.supplier_results.map((result) => (
                  <tr key={result.supplier_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '0.75rem', fontWeight: 'bold', color: '#1e293b' }}>
                      {result.supplier_name}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      <span style={{ 
                        background: result.status === 'Pass' ? '#10b981' : result.status === 'Fail' ? '#ef4444' : '#6b7280',
                        color: 'white', 
                        padding: '0.25rem 0.75rem', 
                        borderRadius: '9999px', 
                        fontSize: '0.75rem', 
                        fontWeight: 'bold' 
                      }}>
                        {result.status}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem', color: '#6b7280', fontSize: '0.9rem' }}>
                      {result.comments || 'No comments'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

  const loadMandatoryAnalysis = async () => {
  setIsLoading(true);
  try {
  const token = Cookies.get('access_token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const response = await axios.get(`${API_URL}/api/reports/process/${processId}/mandatory-analysis`, authHeaders);
  setMandatoryAnalysis(response.data);
} catch (error: any) {
  console.error('Error loading mandatory analysis:', error);
  showNotification('error', 'Failed to load mandatory requirements analysis');
} finally {
  setIsLoading(false);
}
};

  const renderExecutiveDashboard = () => {
    if (!processSummary) return <div>Loading executive dashboard...</div>;

    // Calculate key executive metrics focused on outcomes
    const recommendedSupplier = supplierRankings
      .filter(s => s.compliance_status === 'Compliant')
      .sort((a, b) => {
        if (processSummary?.evaluation_methodology === 'vfm') {
          return (b.vfm_score || 0) - (a.vfm_score || 0);
        }
        return (b.average_score || 0) - (a.average_score || 0);
      })[0];
    
    const recommendedValue = recommendedSupplier?.total_offered_price || 0;
    const totalValue = supplierRankings.reduce((sum, s) => sum + (s.total_offered_price || 0), 0);
    const avgCompetitiveness = supplierRankings.length > 2 ? 'High' : supplierRankings.length > 1 ? 'Medium' : 'Low';
    const processEfficiency = processSummary.completion_percentage;
    const riskLevel = complianceOverview ? 
      (complianceOverview.compliance_rate >= 90 ? 'Low' : 
       complianceOverview.compliance_rate >= 70 ? 'Medium' : 'High') : 'Unknown';

    // Savings calculation
    const avgMarketPrice = totalValue / Math.max(supplierRankings.length, 1);
    const projectedSavings = Math.max(0, avgMarketPrice - recommendedValue);
    const savingsPercentage = avgMarketPrice > 0 ? (projectedSavings / avgMarketPrice) * 100 : 0;

    // Supplier performance distribution for funnel chart
    const totalSuppliers = supplierRankings?.length || 0;
    const compliantSuppliers = supplierRankings?.filter(s => s.compliance_status === 'Compliant').length || 0;
    const evaluatedSuppliers = supplierRankings?.filter(s => s.average_score !== null && s.average_score > 0).length || 0;
    const recommendedSuppliers = supplierRankings?.filter(s => 
      s.compliance_status === 'Compliant' && 
      (s.average_score || 0) >= 70 &&
      s.average_score !== null
    ).length || 0;

    const performanceDistribution = [
      { name: 'Participating Suppliers', value: totalSuppliers, fill: '#8b5cf6' },
      { name: 'Compliant Suppliers', value: compliantSuppliers, fill: '#3b82f6' },
      { name: 'Evaluated Suppliers', value: evaluatedSuppliers, fill: '#10b981' },
      { name: 'Recommended Suppliers', value: recommendedSuppliers, fill: '#059669' }
    ];
    
    // Debug logging for funnel chart data

    // Risk and compliance metrics for C-level
    const avgProcessScore = supplierRankings?.length > 0 
      ? supplierRankings.reduce((sum, s) => sum + (s.average_score || 0), 0) / supplierRankings.length 
      : 0;
    
    const competitionScore = Math.min(100, supplierRankings.length * 20); // 20 points per supplier, max 100
    const qualitySpread = supplierRankings?.length > 1 
      ? Math.max(...supplierRankings.map(s => s.average_score || 0)) - Math.min(...supplierRankings.map(s => s.average_score || 0))
      : 0;

    const riskMetrics = [
      { 
        category: 'Process Risk', 
        level: processEfficiency >= 90 ? 'Low' : processEfficiency >= 70 ? 'Medium' : 'High', 
        score: processEfficiency,
        description: 'Evaluation completion rate across all evaluators',
        calculation: `${processEfficiency.toFixed(0)}% of evaluations completed`
      },
      { 
        category: 'Compliance Risk', 
        level: riskLevel, 
        score: complianceOverview?.compliance_rate || 0,
        description: 'Supplier compliance with mandatory requirements',
        calculation: `${(complianceOverview?.compliance_rate || 0).toFixed(0)}% compliance rate`
      },
      { 
        category: 'Competition Risk', 
        level: supplierRankings.length >= 5 ? 'Low' : supplierRankings.length >= 3 ? 'Medium' : 'High', 
        score: competitionScore,
        description: 'Market competition and supplier participation',
        calculation: `${supplierRankings.length} suppliers participating`
      },
      { 
        category: 'Quality Risk', 
        level: avgProcessScore >= 75 && qualitySpread <= 20 ? 'Low' : avgProcessScore >= 60 ? 'Medium' : 'High', 
        score: avgProcessScore,
        description: 'Quality consistency and evaluation scores',
        calculation: `Avg score: ${avgProcessScore.toFixed(1)}, spread: ${qualitySpread.toFixed(1)}`
      }
    ];

    // Value for Money trends
    const vfmTrends = supplierRankings
      .filter(s => s.vfm_score)
      .sort((a, b) => (b.vfm_score || 0) - (a.vfm_score || 0))
      .map((supplier, index) => ({
        name: supplier.supplier_name.substring(0, 15) + '...',
        vfm: supplier.vfm_score,
        price: supplier.total_offered_price,
        score: supplier.average_score,
        rank: index + 1
      }));

    // Evaluator consistency analysis for probity
    const evaluatorConsistency = evaluatorSummaries.map(evaluator => ({
      name: evaluator.evaluator_name.split(' ')[0],
      avgScore: evaluator.average_score_given || 0,
      completion: evaluator.completion_rate,
      variance: Math.abs((evaluator.average_score_given || 0) - (processSummary.average_process_score || 0))
    }));

    return (
      <div style={{ display: 'grid', gap: '2rem' }}>
        {/* Executive Summary - Business Outcomes */}
        <div style={{ background: 'white', padding: '2.5rem', borderRadius: '16px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', border: '1px solid #e5e7eb' }}>
          <h2 style={{ margin: '0 0 2rem 0', fontSize: '2.2rem', fontWeight: 'bold', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            🎯 Executive Summary
          </h2>
          
          {/* Key Outcome */}
          {recommendedSupplier && (
            <div style={{ 
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
              color: 'white', 
              padding: '1.5rem', 
              borderRadius: '12px', 
              marginBottom: '2rem',
              textAlign: 'center'
            }}>
              <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.4rem' }}>Recommended Supplier</h3>
              <div style={{ fontSize: '1.8rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                {recommendedSupplier.supplier_name}
              </div>
              <div style={{ fontSize: '1.1rem', opacity: 0.9 }}>
                ${recommendedValue.toLocaleString('en-AU')} • Rank #{recommendedSupplier.ranking}
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.5rem' }}>
            <div style={{ textAlign: 'center', padding: '1.5rem', background: '#f8fafc', borderRadius: '12px' }}>
              <div style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#059669' }}>
                ${recommendedValue > 0 ? (recommendedValue / 1000000).toFixed(1) : '0'}M
              </div>
              <div style={{ color: '#6b7280', fontWeight: '500' }}>Recommended Value</div>
            </div>
            <div style={{ textAlign: 'center', padding: '1.5rem', background: '#f8fafc', borderRadius: '12px' }}>
              <div style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#3b82f6' }}>
                {savingsPercentage > 0 ? savingsPercentage.toFixed(1) : '0'}%
              </div>
              <div style={{ color: '#6b7280', fontWeight: '500' }}>Potential Savings</div>
            </div>
            <div style={{ textAlign: 'center', padding: '1.5rem', background: '#f8fafc', borderRadius: '12px' }}>
              <div style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.5rem', color: riskLevel === 'Low' ? '#10b981' : riskLevel === 'Medium' ? '#f59e0b' : '#ef4444' }}>
                {riskLevel}
              </div>
              <div style={{ color: '#6b7280', fontWeight: '500' }}>Compliance Risk</div>
            </div>
            <div style={{ textAlign: 'center', padding: '1.5rem', background: '#f8fafc', borderRadius: '12px' }}>
              <div style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#8b5cf6' }}>
                {processEfficiency.toFixed(0)}%
              </div>
              <div style={{ color: '#6b7280', fontWeight: '500' }}>Process Complete</div>
            </div>
          </div>
          
          {/* Key Insights */}
          <div style={{ marginTop: '2rem', padding: '1.5rem', background: '#fefce8', borderRadius: '12px', border: '1px solid #fcd34d' }}>
            <h4 style={{ margin: '0 0 1rem 0', color: '#92400e', fontSize: '1.1rem', fontWeight: 'bold' }}>Key Insights</h4>
            <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#92400e' }}>
              <li>Total {supplierRankings.length} suppliers evaluated using {processSummary?.evaluation_methodology === 'vfm' ? 'Value for Money' : 'Price/Quality Strategic'} methodology</li>
              <li>{complianceOverview?.compliant_suppliers || 0} suppliers meet mandatory compliance requirements</li>
              {savingsPercentage > 5 && <li>Recommended supplier offers {savingsPercentage.toFixed(1)}% savings vs. market average</li>}
              <li>Evaluation process {processEfficiency.toFixed(0)}% complete across all evaluators</li>
            </ul>
          </div>
        </div>

        {/* Procurement Funnel Analysis */}
        <div style={{ background: 'white', padding: '2rem', borderRadius: '16px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#1e293b', fontSize: '1.5rem' }}>📊 Procurement Process Funnel</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', alignItems: 'center' }}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={performanceDistribution} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={140} />
                <Tooltip 
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const stage = payload[0].payload;
                      let description = '';
                      switch(stage.name) {
                        case 'Participating Suppliers':
                          description = 'Total suppliers registered for this procurement';
                          break;
                        case 'Compliant Suppliers':
                          description = 'Suppliers meeting all mandatory requirements';
                          break;
                        case 'Evaluated Suppliers':
                          description = 'Suppliers with completed quality evaluations';
                          break;
                        case 'Recommended Suppliers':
                          description = 'High-scoring compliant suppliers (≥70 points)';
                          break;
                      }
                      return (
                        <div style={{ background: 'white', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                          <p style={{ margin: 0, fontWeight: 'bold' }}>{stage.name}</p>
                          <p style={{ margin: '0.25rem 0', color: '#6b7280' }}>{description}</p>
                          <p style={{ margin: '0.25rem 0 0 0', color: '#059669', fontWeight: 'bold' }}>Count: {stage.value}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="value" fill="#8884d8">
                  {performanceDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div>
              <h4 style={{ color: '#374151', marginBottom: '1rem' }}>Process Efficiency Metrics</h4>
              {performanceDistribution.map((stage, index) => (
                <div key={stage.name} style={{ marginBottom: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 'bold' }}>{stage.name}</span>
                  <span style={{ background: stage.fill, color: 'white', padding: '0.25rem 0.75rem', borderRadius: '12px', fontSize: '0.9rem' }}>
                    {stage.value} ({index > 0 && performanceDistribution[index-1].value > 0 ? ((stage.value / performanceDistribution[index-1].value) * 100).toFixed(0) : '100'}%)
                  </span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Funnel Explanation */}
          <div style={{ 
            marginTop: '1.5rem', 
            padding: '1rem', 
            background: '#f0f9ff', 
            borderRadius: '8px',
            border: '1px solid #bae6fd'
          }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#0c4a6e', fontSize: '0.95rem' }}>Funnel Stage Definitions</h4>
            <div style={{ fontSize: '0.85rem', color: '#0c4a6e', lineHeight: '1.4' }}>
              <strong>Participating:</strong> All suppliers registered • <strong>Compliant:</strong> Meet mandatory requirements • 
              <strong>Evaluated:</strong> Completed quality scoring • <strong>Recommended:</strong> High performers (≥70/100)
            </div>
          </div>
        </div>

        {/* Risk Dashboard for C-Level */}
        <div style={{ background: 'white', padding: '2rem', borderRadius: '16px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#1e293b', fontSize: '1.5rem' }}>⚠️ Risk Assessment Matrix</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
            {riskMetrics.map(risk => (
              <div key={risk.category} style={{ 
                background: risk.level === 'Low' ? '#ecfdf5' : risk.level === 'Medium' ? '#fffbeb' : '#fef2f2',
                border: `2px solid ${risk.level === 'Low' ? '#10b981' : risk.level === 'Medium' ? '#f59e0b' : '#ef4444'}`,
                padding: '1.5rem', 
                borderRadius: '12px' 
              }}>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151', fontSize: '1.1rem' }}>{risk.category}</h4>
                <div style={{ 
                  fontSize: '1.8rem', 
                  fontWeight: 'bold', 
                  color: risk.level === 'Low' ? '#10b981' : risk.level === 'Medium' ? '#f59e0b' : '#ef4444',
                  marginBottom: '0.75rem' 
                }}>
                  {risk.level}
                </div>
                <div style={{ 
                  color: '#6b7280', 
                  fontSize: '0.9rem',
                  marginBottom: '0.5rem',
                  fontStyle: 'italic'
                }}>
                  {risk.description}
                </div>
                <div style={{ 
                  color: '#374151', 
                  fontSize: '0.85rem',
                  fontWeight: '500'
                }}>
                  {risk.calculation}
                </div>
              </div>
            ))}
          </div>
          
          {/* Risk Assessment Legend */}
          <div style={{ 
            marginTop: '1.5rem', 
            padding: '1rem', 
            background: '#f8fafc', 
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <h4 style={{ margin: '0 0 0.75rem 0', color: '#374151', fontSize: '1rem' }}>Risk Assessment Methodology</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', fontSize: '0.85rem' }}>
              <div>
                <strong>Process Risk:</strong> Based on evaluation completion rates
                <br />• Low: ≥90% • Medium: 70-89% • High: &lt;70%
              </div>
              <div>
                <strong>Compliance Risk:</strong> Supplier mandatory requirement compliance
                <br />• Low: ≥90% • Medium: 70-89% • High: &lt;70%
              </div>
              <div>
                <strong>Competition Risk:</strong> Market participation and choice
                <br />• Low: ≥5 suppliers • Medium: 3-4 suppliers • High: &lt;3 suppliers
              </div>
              <div>
                <strong>Quality Risk:</strong> Score consistency and adequacy
                <br />• Low: Avg ≥75 + spread ≤20 • Medium: Avg ≥60 • High: Avg &lt;60
              </div>
            </div>
          </div>
        </div>

        {/* Strategic Analysis Chart - Methodology-Specific */}
        <div style={{ background: 'white', padding: '2rem', borderRadius: '16px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#1e293b', fontSize: '1.5rem' }}>
            {processSummary?.evaluation_methodology === 'price_quality' ? '📈 Strategic Analysis - Quality vs Cost' : '💰 Value for Money Leadership Board'}
          </h3>
          
          {processSummary?.evaluation_methodology === 'price_quality' && priceQualityAnalysis?.quality_premiums ? (
            <ResponsiveContainer width="100%" height={400}>
              <ScatterChart margin={{ top: 20, right: 80, bottom: 60, left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis 
                  dataKey="cost_difference" 
                  type="number" 
                  domain={['dataMin - 50000', 'dataMax + 50000']}
                  tickFormatter={(value) => `$${(value/1000).toFixed(0)}K`}
                  stroke="#6b7280"
                  fontSize={12}
                />
                <YAxis 
                  dataKey="quality_difference" 
                  type="number" 
                  domain={['dataMin - 2', 'dataMax + 2']}
                  tickFormatter={(value) => `${value.toFixed(1)}`}
                  stroke="#6b7280"
                  fontSize={12}
                />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'cost_difference') return [`$${Number(value).toLocaleString('en-AU')}`, 'Cost Difference'];
                    if (name === 'quality_difference') return [Number(value).toFixed(1), 'Quality Difference'];
                    return [value, name];
                  }}
                  labelFormatter={(label, payload) => payload && payload.length > 0 ? `Supplier: ${payload[0].payload.supplier_name}` : ''}
                  contentStyle={{ 
                    background: 'white', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}
                />
                <ReferenceLine x={0} stroke="#94a3b8" strokeDasharray="2 2" />
                <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="2 2" />
                <Scatter 
                  name="Excellent Value" 
                  data={priceQualityAnalysis.quality_premiums.filter((item: any) => item.premium_category === 'excellent')}
                  fill="#10b981"
                />
                <Scatter 
                  name="Good Value" 
                  data={priceQualityAnalysis.quality_premiums.filter((item: any) => item.premium_category === 'good')}
                  fill="#3b82f6"
                />
                <Scatter 
                  name="Expensive" 
                  data={priceQualityAnalysis.quality_premiums.filter((item: any) => item.premium_category === 'expensive')}
                  fill="#f59e0b"
                />
                <Scatter 
                  name="Very Expensive" 
                  data={priceQualityAnalysis.quality_premiums.filter((item: any) => item.premium_category === 'very_expensive')}
                  fill="#ef4444"
                />
                <Scatter 
                  name="Baseline" 
                  data={priceQualityAnalysis.quality_premiums.filter((item: any) => item.is_baseline)}
                  fill="#6b7280"
                />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  iconType="circle"
                  wrapperStyle={{ fontSize: '12px' }}
                />
              </ScatterChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={vfmTrends.slice(0, 10)} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip 
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div style={{ background: 'white', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                          <p style={{ margin: 0, fontWeight: 'bold' }}>{data.name}</p>
                          <p style={{ margin: '0.25rem 0', color: '#10b981' }}>VFM Score: {data.vfm?.toFixed(2)}</p>
                          <p style={{ margin: '0.25rem 0', color: '#3b82f6' }}>Price: ${data.price?.toLocaleString('en-AU')}</p>
                          <p style={{ margin: '0.25rem 0', color: '#8b5cf6' }}>Quality Score: {data.score?.toFixed(1)}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="vfm" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Process Integrity Dashboard */}
        <div style={{ background: 'white', padding: '2rem', borderRadius: '16px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#1e293b', fontSize: '1.5rem' }}>🛡️ Process Integrity & Compliance</h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
            {/* Evaluator Performance Summary */}
            <div>
              <h4 style={{ color: '#374151', marginBottom: '1rem' }}>Evaluator Performance</h4>
              <div style={{ display: 'grid', gap: '1rem' }}>
                {evaluatorConsistency.map((evaluator, index) => (
                  <div key={index} style={{ 
                    padding: '1rem', 
                    background: '#f8fafc', 
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ fontWeight: 'bold', color: '#1e293b' }}>{evaluator.name}</span>
                      <span style={{ 
                        fontSize: '0.8rem', 
                        padding: '0.25rem 0.5rem', 
                        borderRadius: '4px',
                        background: evaluator.completion === 100 ? '#dcfce7' : '#fef3c7',
                        color: evaluator.completion === 100 ? '#166534' : '#92400e'
                      }}>
                        {evaluator.completion}% Complete
                      </span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.9rem', color: '#6b7280' }}>
                      <span>Avg Score: {evaluator.avgScore?.toFixed(1)}</span>
                      <span>Variance: {evaluator.variance?.toFixed(1)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Process Compliance Checklist */}
            <div>
              <h4 style={{ color: '#374151', marginBottom: '1rem' }}>Process Compliance Status</h4>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div style={{ 
                  padding: '1rem', 
                  background: '#f0fdf4', 
                  borderRadius: '8px',
                  border: '1px solid #bbf7d0'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <span style={{ color: '#10b981', fontSize: '1.2rem' }}>✅</span>
                    <span style={{ fontWeight: 'bold', color: '#166534' }}>Evaluation Independence</span>
                  </div>
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#166534' }}>
                    All evaluators completed assessments independently without consultation
                  </p>
                </div>

                <div style={{ 
                  padding: '1rem', 
                  background: evaluatorConsistency.every(e => e.variance < 15) ? '#f0fdf4' : '#fefce8', 
                  borderRadius: '8px',
                  border: evaluatorConsistency.every(e => e.variance < 15) ? '1px solid #bbf7d0' : '1px solid #fcd34d'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <span style={{ 
                      color: evaluatorConsistency.every(e => e.variance < 15) ? '#10b981' : '#f59e0b', 
                      fontSize: '1.2rem' 
                    }}>
                      {evaluatorConsistency.every(e => e.variance < 15) ? '✅' : '⚠️'}
                    </span>
                    <span style={{ 
                      fontWeight: 'bold', 
                      color: evaluatorConsistency.every(e => e.variance < 15) ? '#166534' : '#92400e'
                    }}>
                      Scoring Consistency
                    </span>
                  </div>
                  <p style={{ 
                    margin: 0, 
                    fontSize: '0.9rem', 
                    color: evaluatorConsistency.every(e => e.variance < 15) ? '#166534' : '#92400e'
                  }}>
                    {evaluatorConsistency.every(e => e.variance < 15) 
                      ? 'All evaluators scored within acceptable variance ranges'
                      : 'Some evaluators show high variance - review recommended'
                    }
                  </p>
                </div>

                <div style={{ 
                  padding: '1rem', 
                  background: '#f0fdf4', 
                  borderRadius: '8px',
                  border: '1px solid #bbf7d0'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <span style={{ color: '#10b981', fontSize: '1.2rem' }}>✅</span>
                    <span style={{ fontWeight: 'bold', color: '#166534' }}>Audit Trail Complete</span>
                  </div>
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#166534' }}>
                    Complete audit trail maintained for all evaluation decisions and changes
                  </p>
                </div>

                <div style={{ 
                  padding: '1rem', 
                  background: (complianceOverview?.compliance_rate || 0) >= 80 ? '#f0fdf4' : '#fefce8', 
                  borderRadius: '8px',
                  border: (complianceOverview?.compliance_rate || 0) >= 80 ? '1px solid #bbf7d0' : '1px solid #fcd34d'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <span style={{ 
                      color: (complianceOverview?.compliance_rate || 0) >= 80 ? '#10b981' : '#f59e0b', 
                      fontSize: '1.2rem' 
                    }}>
                      {(complianceOverview?.compliance_rate || 0) >= 80 ? '✅' : '⚠️'}
                    </span>
                    <span style={{ 
                      fontWeight: 'bold', 
                      color: (complianceOverview?.compliance_rate || 0) >= 80 ? '#166534' : '#92400e'
                    }}>
                      Compliance Verification
                    </span>
                  </div>
                  <p style={{ 
                    margin: 0, 
                    fontSize: '0.9rem', 
                    color: (complianceOverview?.compliance_rate || 0) >= 80 ? '#166534' : '#92400e'
                  }}>
                    {complianceOverview?.compliance_rate || 0}% of suppliers met mandatory compliance requirements
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

    if (isLoading && !processSummary) {
    return (
      <div style={styles.mainContainer} className="density-compact">
        <div style={styles.contentWrapper}>
          <h1 style={styles.pageTitle}>📊 Reports & Analytics</h1>
          <div style={{ textAlign: 'center' }}>
              <div style={{...styles.card, maxWidth: '400px', margin: 'auto'}}>
                  <div style={{ fontSize: 'var(--font-size-lg)', color: 'var(--color-text-primary)' }}>Loading Reports...</div>
              </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.mainContainer} className="density-compact">
      {notification && (
        <div style={{
          position: 'fixed', top: '1rem', right: '1rem', zIndex: 1000,
          background: notification.type === 'success' ? 'var(--color-success)' : 'var(--color-error)',
          color: 'white', padding: '1rem 1.5rem', borderRadius: '12px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
        }}>
          {notification.message}
        </div>
      )}
      
      <div style={styles.contentWrapper}>
        <h1 style={styles.pageTitle}>📊 Reports & Analytics</h1>
        <p style={{ textAlign: 'center', color: 'var(--color-text-on-gradient-secondary)', marginBottom: 'var(--spacing-xl)' }}>{processName}</p>

        <div style={{ background: 'rgba(255,255,255,0.6)', borderBottom: '1px solid #e2e8f0', padding: '0 0.5rem' }}>
        <div style={{ display: 'flex', gap: '0', maxWidth: '1200px', margin: '0 auto', flexWrap: 'nowrap', overflowX: 'auto' }}>
          {[
  { key: 'overview', label: 'Overview' },
  { key: 'executive', label: 'Executive' },
  { key: 'rankings', label: 'Rankings' },
  { key: 'evaluators', label: 'Evaluators' },
  { key: 'mandatory', label: 'Mandatory' },
  { key: 'criteria', label: 'Criteria' },
    { key: 'compliance', label: 'Compliance' },
    { key: 'audit-report', label: 'Audit' }
].map(tab => (
            <button
              key={tab.key}
              onClick={() => setCurrentView(tab.key as any)}
              style={{
                padding: '0.5rem 0.5rem',
                background: 'transparent',
                color: currentView === tab.key ? '#2563eb' : '#6b7280',
                border: 'none',
                borderBottom: currentView === tab.key ? '3px solid #2563eb' : '3px solid transparent',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: '600',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
                minWidth: 'fit-content',
                flex: '0 0 auto'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      
        <div style={{ padding: 'var(--spacing-xl)' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '2rem'}}>
                <div style={{display: 'flex', gap: '1rem'}}>
                    <div style={{position: 'relative', display: 'inline-block'}}>
                        <button 
                            onClick={() => setShowExportDropdown(!showExportDropdown)} 
                            style={{...styles.button, background: 'linear-gradient(45deg, #8b5cf6, #7c3aed)', display: 'flex', alignItems: 'center', gap: '0.5rem'}}
                        >
                            📥 Export Data <span style={{fontSize: '0.8rem'}}>▼</span>
                        </button>
                        {showExportDropdown && (
                            <div style={{
                                position: 'absolute', 
                                top: '100%', 
                                right: 0, 
                                zIndex: 1000,
                                background: 'white', 
                                border: '1px solid #e2e8f0', 
                                borderRadius: '8px',
                                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                                minWidth: '150px'
                            }}>
                                <button onClick={() => {exportData('json'); setShowExportDropdown(false);}} style={{...styles.dropdownItem}}>📄 JSON</button>
                                <button onClick={() => {exportData('csv'); setShowExportDropdown(false);}} style={{...styles.dropdownItem}}>📊 CSV</button>
                                <button onClick={() => {exportData('xlsx'); setShowExportDropdown(false);}} style={{...styles.dropdownItem}}>📈 XLSX</button>
                                <button onClick={() => {exportData('pdf'); setShowExportDropdown(false);}} style={{...styles.dropdownItem}}>📄 PDF</button>
                            </div>
                        )}
                    </div>
                    <button onClick={onBack} style={{...styles.button, ...styles.buttonSecondary}}>← Back to Dashboard</button>
                </div>
            </div>

            {isLoading && <div style={{...styles.card, textAlign: 'center'}}>Loading report data...</div>}
            {!isLoading && (
                <>
                    {currentView === 'overview' && <div style={styles.card}>{renderOverview()}</div>}
                    {currentView === 'executive' && <div style={styles.card}>{renderExecutiveDashboard()}</div>}
                    {currentView === 'rankings' && <div style={styles.card}>{renderSupplierRankings()}</div>}
                    {currentView === 'evaluators' && <div style={styles.card}>{renderEvaluatorSummaries()}</div>}
                    {currentView === 'criteria' && <div style={styles.card}>{renderCriterionAnalysis()}</div>}
                    {currentView === 'compliance' && <div style={styles.card}>{renderComplianceOverview()}</div>}
                    {currentView === 'mandatory' && <div style={styles.card}>{renderMandatoryAnalysis()}</div>}
                    {currentView === 'audit-report' && <ComprehensiveAuditReport processId={processId} processName={processName} userRole={userRole} onBack={() => setCurrentView('overview')} />}
                </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsDashboard;
